import { type ActionEvent, Controller } from "@hotwired/stimulus"

import { formatISO } from "@/helpers/date_format"

const SELECTED_RANGE_CLASS = "in-selected-range"
const FIRST_IN_SELECTED_RANGE_CLASS = "first-in-selected-range"
const LAST_IN_SELECTED_RANGE_CLASS = "last-in-selected-range"

const removeSelectedClasses = (radio: HTMLInputElement): void => {
  radio.classList.remove(
    FIRST_IN_SELECTED_RANGE_CLASS,
    LAST_IN_SELECTED_RANGE_CLASS,
    SELECTED_RANGE_CLASS
  )
}

export default class extends Controller {
  static targets = ["dateRadio"]
  declare readonly dateRadioTargets: HTMLInputElement[]

  static values = { numberOfNights: Number }
  declare numberOfNightsValue: number

  // Manually track selected radio so we can un-check it on click.
  declare selectedDateRadio?: HTMLInputElement

  connect(): void {
    this.selectedDateRadio = this.dateRadioTargets.find((radio) => radio.checked)

    if (this.selectedDateRadio) {
      this.highlightDateRange(this.selectedDateRadio)
    }
  }

  dateSelected(event: ActionEvent): void {
    const dateRadio = event.currentTarget as HTMLInputElement

    // Un-check radio if already selected
    if (dateRadio === this.selectedDateRadio) {
      dateRadio.checked = false
      dateRadio.dispatchEvent(new Event("change"))
      dateRadio.blur()
      this.selectedDateRadio = undefined
      this.clearHighlight()
    } else {
      this.selectedDateRadio = dateRadio
      this.highlightDateRange(this.selectedDateRadio)
    }
  }

  private clearHighlight(): void {
    this.dateRadioTargets.forEach(removeSelectedClasses)
  }

  private highlightDateRange(target: HTMLInputElement): void {
    if (this.numberOfNightsValue === 0) {
      return
    }

    const targetDate = new Date(target.value)
    const startDate = new Date(targetDate)
    const endDate = new Date(targetDate.setDate(targetDate.getDate() + this.numberOfNightsValue))
    const endDateRadio = this.dateRadioTargets.find((radio) => radio.value === formatISO(endDate))

    this.dateRadioTargets.forEach((radio) => {
      const date = new Date(radio.value)

      removeSelectedClasses(radio)

      if (radio === target) {
        radio.classList.add(FIRST_IN_SELECTED_RANGE_CLASS)
      } else if (radio.value === endDateRadio?.value) {
        radio.classList.add(LAST_IN_SELECTED_RANGE_CLASS)
      } else if (date > startDate && date < endDate) {
        radio.classList.add(SELECTED_RANGE_CLASS)
      }
    })
  }
}
